import React, { useCallback } from "react";
import { twMerge } from "tailwind-merge";
import AppButton from "./AppButton.jsx";
import { sanitizeFileUrl } from "../../utils/filePreview.js";
import { showError } from "../../utils/toast.js";
import {
  openPdfExportPreview,
  PdfExportPreviewModal,
} from "../../utils/pdfExportPreviewHost.jsx";

export { PdfExportPreviewModal };

const defaultMenuRowCls =
  "flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-muted min-h-[44px] text-primary";

/**
 * @deprecated Nutze PdfExportPreviewModal – gleiches Verhalten, kein Zwischen-Sheet mehr.
 */
export function PdfExportActionSheet({
  open,
  onClose,
  pdfUrl,
  title = "PDF",
  fileName = "export.pdf",
  previewModule = "",
  fileId = null,
}) {
  return (
    <PdfExportPreviewModal
      open={open}
      onClose={onClose}
      pdfUrl={pdfUrl}
      title={title}
      fileName={fileName}
      previewModule={previewModule}
      fileId={fileId}
    />
  );
}

/**
 * Auslöser für Export-PDFs: öffnet immer die Vorschau (kein Direkt-Tab mehr).
 */
export default function PdfExportTrigger({
  pdfUrl,
  title = "PDF",
  fileName = "export.pdf",
  triggerLabel = "PDF",
  variant = "secondary",
  size = "sm",
  className = "",
  disabled = false,
  children,
  onBeforeOpen,
  menuItem = false,
  menuItemClassName,
  nativeLinkAppearance = false,
  type = "button",
  previewDescription = "",
}) {
  const safeUrl = sanitizeFileUrl(pdfUrl);

  const openPreview = useCallback(() => {
    if (!safeUrl || disabled) return;
    const opened = openPdfExportPreview({
      pdfUrl,
      title,
      fileName,
      description: previewDescription,
    });
    onBeforeOpen?.();
    if (!opened) {
      showError("PDF-Vorschau konnte nicht geöffnet werden. Seite neu laden.");
      window.open(safeUrl, "_blank", "noopener,noreferrer");
    }
  }, [safeUrl, disabled, pdfUrl, title, fileName, previewDescription, onBeforeOpen]);

  if (!safeUrl || disabled) {
    if (menuItem) {
      return (
        <span
          className={twMerge("opacity-45 cursor-not-allowed", menuItemClassName || defaultMenuRowCls, className)}
          role="presentation"
        >
          {children ?? triggerLabel}
        </span>
      );
    }
    return (
      <AppButton type="button" variant={variant} size={size} disabled className={className}>
        {children ?? triggerLabel}
      </AppButton>
    );
  }

  const trigger =
    menuItem ? (
      <AppButton
        type="button"
        role="menuitem"
        variant="ghost"
        size="sm"
        className={twMerge(menuItemClassName || defaultMenuRowCls, className)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          openPreview();
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children ?? triggerLabel}
      </AppButton>
    ) : nativeLinkAppearance ? (
      <AppButton
        type="button"
        variant="ghost"
        size="lg"
        className={twMerge("!border-0 !bg-transparent shadow-none min-h-[44px]", className)}
        onClick={openPreview}
        title={triggerLabel}
        aria-label={triggerLabel}
      >
        {children ?? triggerLabel}
      </AppButton>
    ) : (
      <AppButton
        type={type === "submit" ? "submit" : "button"}
        variant={variant}
        size={size}
        className={twMerge("min-h-[44px] md:min-h-0", className)}
        onClick={openPreview}
        title={triggerLabel}
        aria-label={triggerLabel}
      >
        {children ?? triggerLabel}
      </AppButton>
    );

  return trigger;
}
